import React, {useEffect, useState} from "react";
import Layout from "../Layout";
import {Button, Card, Descriptions, Form, Input, message, Modal, Space, Table,} from "antd";
import {FaEye, FaEyeSlash} from "react-icons/fa";

const TeacherView = () => {
    const [teachers, setTeachers] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");

    // Add Teacher Modal
    const [addModalVisible, setAddModalVisible] = useState(false);

    // Detail Modal
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [selectedTeacher, setSelectedTeacher] = useState(null);

    // Create new teacher data
    const [newTeacherData, setNewTeacherData] = useState({
        name: "",
        email: "",
        password: "",
        role: "teacher",
    });
    const [passwordShown, setPasswordShown] = useState(false);

    // Constants
    const server_url = "https://api.edumonics.in";
    const accessToken = localStorage.getItem("accessToken");

    // Toggle password visibility
    const togglePasswordVisibility = () =>
        setPasswordShown((prev) => !prev);

    // Search Filter
    const handleSearch = (e) => {
        setSearchTerm(e.target.value.toLowerCase());
    };

    // Filter teachers by name, email, role, or current_school
    const filteredTeachers = teachers.filter(
        (t) =>
            (t.name && t.name.toLowerCase().includes(searchTerm)) ||
            (t.email && t.email.toLowerCase().includes(searchTerm)) ||
            (t.role && t.role.toLowerCase().includes(searchTerm)) ||
            (t.current_school &&
                t.current_school.toLowerCase().includes(searchTerm))
    );

    // Fetch teachers from /admin/get-teachers
    const fetchTeachers = async () => {
        try {
            const response = await fetch(`${server_url}/admin/get-teachers`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || "Failed to fetch teachers");
            }
            const data = await response.json();
            setTeachers(data);
        } catch (error) {
            console.error("Error fetching teachers:", error);
            message.error(error.message || "Error fetching teachers.");
        }
    };

    useEffect(() => {
        fetchTeachers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Create new teacher
    const handleCreateTeacher = async () => {
        try {
            const response = await fetch(`${server_url}/admin/create-user`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(newTeacherData),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.detail || "Failed to create teacher");
            }
            message.success(data.message || "Teacher created successfully!");
            setAddModalVisible(false);
            fetchTeachers();
        } catch (error) {
            console.error("Error creating teacher:", error);
            message.error(error.message || "Error creating teacher.");
        }
    };

    // Delete Teacher
    const deleteTeacher = async (email) => {
        const confirmed = window.confirm(
            "Are you sure you want to delete this teacher?"
        );
        if (!confirmed) return;

        try {
            const response = await fetch(
                `${server_url}/admin/delete-user/${email}`,
                {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.detail || "Failed to delete teacher");
            }
            message.success(data.detail);
            setTeachers((prev) => prev.filter((teacher) => teacher.email !== email));
        } catch (error) {
            console.error("Error deleting teacher:", error);
            message.error(error.message || "Error deleting teacher.");
        }
    };

    // Table Columns
    const columns = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            render: (text) => text || "--",
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
            render: (text) => text || "--",
        },
        {
            title: "Role",
            dataIndex: "role",
            key: "role",
            render: (text) => text || "--",
        },
        {
            title: "Current School",
            dataIndex: "current_school",
            key: "current_school",
            render: (text) => text || "--",
        },
        {
            title: "Experience",
            dataIndex: "years_of_experience",
            key: "years_of_experience",
            render: (val) => (val ? `${val} yrs` : "0 yrs"),
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            key: "created_at",
            render: (val) =>
                val ? new Date(val).toLocaleString() : "--",
        },
        {
            title: "Action",
            key: "action",
            render: (teacher) => (
                <Space>
                    <Button
                        type="primary"
                        onClick={() => {
                            setSelectedTeacher(teacher);
                            setDetailModalVisible(true);
                        }}
                        className="bg-yellow-400 hover:bg-yellow-500 text-black p-2 rounded-md"

                    >
                        View
                    </Button>
                    <Button
                        type="text"
                        danger
                        onClick={() => deleteTeacher(teacher.email)}
                    >
                        Delete
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <Layout pageTitle="View Teachers">
            <div className="mx-auto px-6 py-8">
                <h1 className="text-3xl font-semibold text-center text-gray-800 mb-6">
                    Teacher Accounts
                </h1>

                {/* Search Input */}
                <div className="flex justify-center mb-4">
                    <Input
                        placeholder="Search by Name, Email, Role, or School..."
                        style={{width: "50%",fontFamily: "Gotham, sans-serif",}}
                        onChange={handleSearch}
                    />
                </div>

                {/* Create Teacher Button */}
                <div className="flex justify-end mb-4">
                    <Button
                        type="primary"
                        className=" bg-yellow-400 hover:bg-yellow-500 text-black p-2 rounded-md"
                        onClick={() => setAddModalVisible(true)}
                    >
                        Create New Teacher
                    </Button>
                </div>

                {/* Teacher Table */}
                <Table
                    dataSource={filteredTeachers}
                    columns={columns}
                    rowKey="id"
                    bordered
                />

                {/* Modal: Add Teacher */}
                <Modal
                    title="Add New Teacher"
                    visible={addModalVisible}
                    onCancel={() => setAddModalVisible(false)}
                    footer={null}
                    destroyOnClose
                >
                    <Form
                        layout="vertical"
                        onFinish={handleCreateTeacher}
                    >
                        <Form.Item
                            label="Name"
                            required
                            tooltip="Enter the teacher's full name"
                        >
                            <Input
                                name="name"
                                value={newTeacherData.name}
                                onChange={(e) =>
                                    setNewTeacherData({
                                        ...newTeacherData,
                                        name: e.target.value,
                                    })
                                }
                                placeholder="John Doe"
                            />
                        </Form.Item>

                        <Form.Item
                            label="Email"
                            required
                            tooltip="Teacher's email address"
                        >
                            <Input
                                type="email"
                                name="email"
                                value={newTeacherData.email}
                                onChange={(e) =>
                                    setNewTeacherData({
                                        ...newTeacherData,
                                        email: e.target.value,
                                    })
                                }
                                placeholder="johndoe@example.com"
                            />
                        </Form.Item>

                        <Form.Item
                            label="Password"
                            required
                            tooltip="Set an initial password"
                        >
                            <div style={{position: "relative",fontFamily: "Gotham, sans-serif",}}>
                                <Input
                                    type={passwordShown ? "text" : "password"}
                                    name="password"
                                    value={newTeacherData.password}
                                    onChange={(e) =>
                                        setNewTeacherData({
                                            ...newTeacherData,
                                            password: e.target.value,
                                        })
                                    }
                                    placeholder="••••••••"
                                />
                                <span
                                    style={{
                                        position: "absolute",
                                        right: 10,
                                        top: "30%",
                                        cursor: "pointer",
                                        fontFamily: "Gotham, sans-serif",
                                    }}
                                    onClick={togglePasswordVisibility}
                                >
                  {passwordShown ? <FaEye/> : <FaEyeSlash/>}
                </span>
                            </div>
                        </Form.Item>

                        <Form.Item label="Role">
                            <Input
                                value="teacher"
                                disabled
                            />
                        </Form.Item>

                        <Form.Item>
                            <Button type="primary" htmlType="submit" block
                                    className=" bg-yellow-400 hover:bg-yellow-500 text-black p-2 rounded-md"
                            >

                                Add Teacher
                            </Button>
                        </Form.Item>
                    </Form>
                </Modal>

                {/* Modal: Teacher Details */}
                <Modal
                    title="Teacher Details"
                    visible={detailModalVisible}
                    onCancel={() => setDetailModalVisible(false)}
                    footer={null}
                    destroyOnClose
                    width={800}
                >
                    {selectedTeacher && (
                        <Card
                            bordered={false}
                            style={{maxHeight: "70vh", overflowY: "auto",fontFamily: "Gotham, sans-serif",}}
                        >
                            {/* Ant Design Descriptions for structured data */}
                            <Descriptions
                                layout="vertical"
                                column={{xs: 1, sm: 2, md: 2}}
                                bordered
                                size="small"
                                labelStyle={{fontWeight: "bold", width: 150,fontFamily: "Gotham, sans-serif",}}
                            >
                                {/* Basic Info */}
                                <Descriptions.Item label="Name">
                                    {selectedTeacher.name || "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Email">
                                    {selectedTeacher.email || "--"}
                                </Descriptions.Item>

                                <Descriptions.Item label="Role">
                                    {selectedTeacher.role || "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Created At">
                                    {selectedTeacher.created_at
                                        ? new Date(selectedTeacher.created_at).toLocaleString()
                                        : "--"}
                                </Descriptions.Item>

                                {/* Teacher-Profile Fields */}
                                <Descriptions.Item label="Phone Number">
                                    {selectedTeacher.phone_number || "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Residence">
                                    {selectedTeacher.residence || "--"}
                                </Descriptions.Item>

                                <Descriptions.Item label="Experience">
                                    {selectedTeacher.years_of_experience
                                        ? `${selectedTeacher.years_of_experience} yrs`
                                        : "0 yrs"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Certifications">
                                    {selectedTeacher.certifications &&
                                    selectedTeacher.certifications.length
                                        ? selectedTeacher.certifications.join(", ")
                                        : "--"}
                                </Descriptions.Item>

                                <Descriptions.Item label="Current School">
                                    {selectedTeacher.current_school || "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Past Schools">
                                    {selectedTeacher.past_schools &&
                                    selectedTeacher.past_schools.length
                                        ? selectedTeacher.past_schools.join(", ")
                                        : "--"}
                                </Descriptions.Item>

                                <Descriptions.Item label="Areas of Expertise">
                                    {selectedTeacher.areas_of_expertise &&
                                    selectedTeacher.areas_of_expertise.length
                                        ? selectedTeacher.areas_of_expertise.join(", ")
                                        : "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Date of Birth">
                                    {selectedTeacher.date_of_birth || "--"}
                                </Descriptions.Item>

                                <Descriptions.Item label="Gender">
                                    {selectedTeacher.gender || "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Languages Spoken">
                                    {selectedTeacher.languages_spoken &&
                                    selectedTeacher.languages_spoken.length
                                        ? selectedTeacher.languages_spoken.join(", ")
                                        : "--"}
                                </Descriptions.Item>

                                <Descriptions.Item label="Preferred Methods">
                                    {selectedTeacher.preferred_teaching_methods &&
                                    selectedTeacher.preferred_teaching_methods.length
                                        ? selectedTeacher.preferred_teaching_methods.join(", ")
                                        : "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Availability">
                                    {selectedTeacher.availability &&
                                    selectedTeacher.availability.length
                                        ? selectedTeacher.availability.join(", ")
                                        : "--"}
                                </Descriptions.Item>

                                <Descriptions.Item label="Grade Levels">
                                    {selectedTeacher.grade_levels &&
                                    selectedTeacher.grade_levels.length
                                        ? selectedTeacher.grade_levels.join(", ")
                                        : "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Awards">
                                    {selectedTeacher.awards && selectedTeacher.awards.length
                                        ? selectedTeacher.awards.join(", ")
                                        : "--"}
                                </Descriptions.Item>

                                <Descriptions.Item label="Notable Projects">
                                    {selectedTeacher.notable_projects &&
                                    selectedTeacher.notable_projects.length
                                        ? selectedTeacher.notable_projects.join(", ")
                                        : "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Quizzes Created">
                                    {selectedTeacher.num_quizzes_created || 0}
                                </Descriptions.Item>

                                <Descriptions.Item label="Study Materials Uploaded">
                                    {selectedTeacher.study_materials_uploaded || 0}
                                </Descriptions.Item>
                                <Descriptions.Item label="Extracurricular Activities">
                                    {selectedTeacher.extracurricular_activities &&
                                    selectedTeacher.extracurricular_activities.length
                                        ? selectedTeacher.extracurricular_activities.join(", ")
                                        : "--"}
                                </Descriptions.Item>

                                <Descriptions.Item label="Tech Skills">
                                    {selectedTeacher.tech_skills && selectedTeacher.tech_skills.length
                                        ? selectedTeacher.tech_skills.join(", ")
                                        : "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Student Reviews">
                                    {selectedTeacher.student_reviews &&
                                    selectedTeacher.student_reviews.length
                                        ? selectedTeacher.student_reviews.join(" | ")
                                        : "--"}
                                </Descriptions.Item>

                                <Descriptions.Item label="Ratings">
                                    {selectedTeacher.ratings !== undefined
                                        ? selectedTeacher.ratings
                                        : "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Profile Picture">
                                    {selectedTeacher.profile_picture || "--"}
                                </Descriptions.Item>

                                <Descriptions.Item label="Biography" span={2}>
                                    {selectedTeacher.biography || "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Video Intro">
                                    {selectedTeacher.video_intro || "--"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Career Objectives">
                                    {selectedTeacher.career_objectives || "--"}
                                </Descriptions.Item>

                                <Descriptions.Item label="Professional Dev Areas" span={2}>
                                    {selectedTeacher.professional_development_areas &&
                                    selectedTeacher.professional_development_areas.length
                                        ? selectedTeacher.professional_development_areas.join(", ")
                                        : "--"}
                                </Descriptions.Item>
                            </Descriptions>
                        </Card>
                    )}
                </Modal>
            </div>
        </Layout>
    );
};

export default TeacherView;
